import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { playerService, agentStaffService } from "../../services/api";
import AgentPlayerCard from "../../components/agent/AgentPlayerCard";
import AgentStaffCard from "../../components/agent/AgentStaffCard";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import MySquadMobileHeader from "../../components/agent/responsive/MySquadMobileHeader";
// import MySquadMobilePositions from "../../components/agent/responsive/MySquadMobilePositions";
import PositionFilterDropdown from "../../components/common/PositionFilterDropdown";
import { DETAILED_POSITION_OPTIONS } from "../../components/common/DetailedPositionsControl";
import { useMyListedPlayers } from "../../hooks/last-minute/useMyListedPlayers";
import { STAFF_ROLE_CHOICES } from "../../constants/lastMinuteChoices";
import { MYSTAFF_ENABLED, STAFF_MANDATE_ENABLED } from "../../config/featureFlags";


const MySquad = () => {
  // Which squad players are already on Last Minute (design 6a already-listed state).
  const lmListedByPlayerId = useMyListedPlayers();
  const [selectedPosition, setSelectedPosition] = useState("all");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // TP-MyStaff P1: Players | Staff subject switch (feature-flagged). Flag off ⇒
  // subject is pinned to "players" and none of the staff branch renders, so the
  // player surface is byte-for-byte unchanged.
  const [subject, setSubject] = useState("players");
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);
  const [selectedRole, setSelectedRole] = useState("all");

  // Role filter options for the staff view, from the shared staff-role catalog.
  const roleOptions = [
    { id: "all", label: "All roles" },
    ...STAFF_ROLE_CHOICES.map((r) => ({ id: r.value, label: r.label })),
  ];

  const fetchStaff = useCallback(async () => {
    setStaffLoading(true);
    setStaffError(null);
    const controller = new AbortController();
    try {
      const response = await agentStaffService.getStaffList(
        undefined,
        controller.signal
      );
      if (response.success) {
        setStaffList(response.data);
      } else {
        setStaffError(response.error);
      }
    } catch (err) {
      if (err.name === "AbortError" || err.name === "CanceledError") return;
      setStaffError("Failed to fetch staff. Please try again.");
    } finally {
      setStaffLoading(false);
    }
  }, []);

  useEffect(() => {
    if (MYSTAFF_ENABLED && subject === "staff") {
      fetchStaff();
    }
  }, [subject, fetchStaff]);

  // Client-side role filter (P1). Reads the staff role token defensively.
  const filteredStaff = useMemo(() => {
    if (selectedRole === "all") return staffList;
    return staffList.filter(
      (s) => (s.staff_type || s.role_type) === selectedRole
    );
  }, [staffList, selectedRole]);

  // Create positions array with all detailed positions
  const positions = [
    { id: "all", label: "All positions" },
    { id: "goalkeeper", label: "Goalkeeper" },
    { id: "defender", label: "Defender" },
    { id: "midfielder", label: "Midfielder" },
    { id: "forward", label: "Forward" },
    ...DETAILED_POSITION_OPTIONS.map(option => ({
      id: option.value,
      label: option.value.toUpperCase(),
      fullLabel: option.label
    })),
    { id: "unknown", label: "Position not set" },
  ];

  const fetchPlayers = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      const controller = new AbortController();

      try {
        const params = new URLSearchParams();
        params.append("page", page);

        // Add position filter if not 'all'
        if (selectedPosition !== "all") {
          params.append("position", selectedPosition);
        }

        // Add search query if exists
        if (searchQuery) {
          params.append("search", searchQuery);
        }

        const response = await playerService.getPlayers(
          params,
          controller.signal
        );

        if (response.success) {
          setPlayers(response.data);
          setPagination(response.pagination);
        } else {
          setError(response.error);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to fetch players. Please try again.");
      } finally {
        setLoading(false);
      }

      return () => controller.abort();
    },
    [selectedPosition, searchQuery]
  );

  useEffect(() => {
    const abortController = new AbortController();
    fetchPlayers(pagination.currentPage);
    return () => abortController.abort();
  }, [fetchPlayers, pagination.currentPage]);

  const handleDetailedPositionsUpdate = useCallback(
    (updatedPlayer, updatedPositions) => {
      setPlayers((prev) =>
        prev.map((p) => {
          const matches =
            p.id === updatedPlayer.id ||
            p.player_id === updatedPlayer.player_id ||
            p.account_id === updatedPlayer.account_id ||
            p.wyscout_player_id === updatedPlayer.wyscout_player_id ||
            p.ws_player_id === updatedPlayer.ws_player_id;

          if (!matches) return p;

          return {
            ...p,
            tp_detailed_position: updatedPositions,
            tpDetailedPosition: updatedPositions,
          };
        })
      );
    },
    []
  );

  const handleContractExpiryUpdate = useCallback(
    (updatedPlayer, contractExpiryDate) => {
      setPlayers((prev) =>
        prev.map((p) => {
          const matches =
            p.id === updatedPlayer.id ||
            p.player_id === updatedPlayer.player_id ||
            p.account_id === updatedPlayer.account_id ||
            p.wyscout_player_id === updatedPlayer.wyscout_player_id ||
            p.ws_player_id === updatedPlayer.ws_player_id;

          if (!matches) return p;

          return {
            ...p,
            contract_expires_on: contractExpiryDate,
            contractExpiresOn: contractExpiryDate,
          };
        })
      );
    },
    []
  );

  // Optimized filtering function
  const filterPlayersByPosition = useCallback((players, position) => {
    if (position === "all") return players;

    return players.filter((player) => {
      const playerPosition = player.position || "";

      // Handle the special case for unknown positions
      if (position === "unknown") {
        return (
          playerPosition === "Position not set" ||
          playerPosition === "" ||
          !playerPosition
        );
      }

      // Check if player has detailed positions that match the selected position code
      const playerDetailedPositions = player.tp_detailed_position || player.tpDetailedPosition || {};
      const hasMatchingDetailedPosition = 
        playerDetailedPositions.primary_position === position.toUpperCase() ||
        playerDetailedPositions.secondary_position === position.toUpperCase() ||
        playerDetailedPositions.teritory_position === position.toUpperCase() ||
        playerDetailedPositions.tertiary_position === position.toUpperCase();

      // If player has detailed positions, only use those for filtering
      if (playerDetailedPositions.primary_position || playerDetailedPositions.secondary_position || 
          playerDetailedPositions.teritory_position || playerDetailedPositions.tertiary_position) {
        return hasMatchingDetailedPosition;
      }

      // For players without detailed positions, map general categories to specific codes
      const positionLower = playerPosition.toLowerCase().trim();
      const selectedLower = position.toLowerCase();
      
      // Map general positions to detailed position codes
      // Check if the general position category includes the selected detailed position
      const generalToDetailedMapping = {
        'goalkeeper': ['gk'],
        'defender': ['rb', 'rcb', 'lcb', 'cb', 'lb', 'rwb', 'lwb', 'rb5', 'lb5', 'rcb3', 'lcb3'],
        'midfielder': ['cm', 'rcmf', 'lcmf', 'rcmf3', 'lcmf3', 'dmf', 'rdmf', 'ldmf', 'amf', 'ramf', 'lamf'],
        'forward': ['rw', 'lw', 'rwf', 'lwf', 'ss', 'cf'],
        'attacker': ['rw', 'lw', 'rwf', 'lwf', 'ss', 'cf']
      };

      // Check if the player's general position includes the selected detailed code
      const detailedCodes = generalToDetailedMapping[positionLower] || [];
      if (detailedCodes.includes(selectedLower)) {
        return true;
      }

      // Also check exact position name mappings for specific cases
      const exactMappings = {
        'gk': ['goalkeeper'],
        'rb': ['right back', 'right-back'],
        'rcb': ['right centre back', 'right center back'],
        'lcb': ['left centre back', 'left center back'],
        'cb': ['centre back', 'center back', 'central defender'],
        'lb': ['left back', 'left-back'],
        'rwb': ['right wing back', 'right wingback'],
        'lwb': ['left wing back', 'left wingback'],
        'cm': ['centre midfielder', 'center midfielder', 'central midfielder'],
        'dmf': ['defensive midfielder'],
        'amf': ['attacking midfielder'],
        'rw': ['right winger', 'right wing'],
        'lw': ['left winger', 'left wing'],
        'cf': ['centre forward', 'center forward', 'striker', 'central forward'],
        'ss': ['second striker', 'support striker']
      };

      const exactTerms = exactMappings[selectedLower] || [];
      return exactTerms.some(term => positionLower === term);
    });
  }, []);

  // Filter players by position using the optimized function
  const filteredPlayers = useMemo(() => {
    return filterPlayersByPosition(players, selectedPosition);
  }, [players, selectedPosition, filterPlayersByPosition]);

  // Sort players by GK -> DEF -> MID -> FWD
  const sortedPlayers = useMemo(() => {
    const GENERAL_ORDER = ["goalkeeper", "defender", "midfielder", "forward"]; // attacker treated as forward

    const generalToDetailedMapping = {
      goalkeeper: ["gk"],
      defender: [
        "rb",
        "rcb",
        "lcb",
        "cb",
        "lb",
        "rwb",
        "lwb",
        "rb5",
        "lb5",
        "rcb3",
        "lcb3",
      ],
      midfielder: [
        "cm",
        "rcmf",
        "lcmf",
        "rcmf3",
        "lcmf3",
        "dmf",
        "rdmf",
        "ldmf",
        "amf",
        "ramf",
        "lamf",
      ],
      forward: ["rw", "lw", "rwf", "lwf", "ss", "cf"],
      attacker: ["rw", "lw", "rwf", "lwf", "ss", "cf"],
    };

    const codeToGeneral = Object.entries(generalToDetailedMapping).reduce(
      (acc, [general, codes]) => {
        codes.forEach((code) => {
          acc[code] = general;
        });
        return acc;
      },
      {}
    );

    const getGeneralCategory = (player) => {
      const dp = player.tp_detailed_position || player.tpDetailedPosition || {};
      const candidateCodes = [
        dp.primary_position,
        dp.secondary_position,
        dp.teritory_position,
        dp.tertiary_position,
      ]
        .filter(Boolean)
        .map((c) => String(c).toLowerCase());

      for (const code of candidateCodes) {
        const general = codeToGeneral[code];
        if (general) return general;
      }

      const pos = (player.position || "").toLowerCase();
      if (pos.includes("goalkeeper")) return "goalkeeper";
      if (
        pos.includes("defender") ||
        pos.includes("back") ||
        pos.includes("wingback")
      )
        return "defender";
      if (pos.includes("midfielder") || pos.includes("midfield"))
        return "midfielder";
      if (
        pos.includes("forward") ||
        pos.includes("attacker") ||
        pos.includes("striker") ||
        pos.includes("winger")
      )
        return "forward";
      return "unknown";
    };

    const rank = (general) => {
      const idx = GENERAL_ORDER.indexOf(general);
      return idx === -1 ? GENERAL_ORDER.length : idx;
    };

    return [...filteredPlayers].sort((a, b) => {
      const ra = rank(getGeneralCategory(a));
      const rb = rank(getGeneralCategory(b));
      return ra - rb;
    });
  }, [filteredPlayers]);

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      {isMobile ? (
        <MySquadMobileHeader />
      ) : (
        <div className="border-b bg-white">
          <div className=" mx-auto px-8">
            <div className="flex justify-between items-center py-4">
              <h1 className="text-xl font-semibold">My Players</h1>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/agent/invite-player")}
                  className="px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Invite player
                </button>
                {/* TP-Coach-Mandate: "Invite staff" sits DIRECTLY NEXT TO
                    "Invite player" (Brief §2.5 locked). Flag-gated; flag off
                    ⇒ the player surface is unchanged. */}
                {STAFF_MANDATE_ENABLED && (
                  <button
                    onClick={() => navigate("/agent/invite-staff")}
                    className="px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Invite staff
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TP-MyStaff P1: Players | Staff segmented switch (feature-flagged). */}
      {MYSTAFF_ENABLED && (
        <div className="mx-auto px-4 md:px-8 pt-4 md:pt-6">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[#F2F4F7]">
            {[
              { id: "players", label: "Players" },
              { id: "staff", label: "Staff" },
            ].map((tab) => {
              const active = subject === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSubject(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    active
                      ? "bg-[#101828] text-white"
                      : "bg-transparent text-[#475467] hover:text-[#101828]"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {MYSTAFF_ENABLED && subject === "staff" ? (
        <>
          {/* Role Filter */}
          <div className=" mx-auto px-4 md:px-8 py-4 md:py-6">
            <PositionFilterDropdown
              positions={roleOptions}
              selectedId={selectedRole}
              onSelect={setSelectedRole}
              placeholder="Select role"
            />
          </div>

          {/* Staff Content */}
          <div className={`mx-auto px-4 md:px-12 mt-4 md:mt-0`}>
            {staffLoading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}

            {staffError && (
              <div className="text-red-500 text-center py-8">{staffError}</div>
            )}

            {!staffLoading && !staffError && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredStaff.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    {selectedRole === "all"
                      ? "No staff found in your squad"
                      : "No staff found for this role"}
                  </div>
                ) : (
                  filteredStaff.map((member, index) => (
                    // External (pre-registration) mandate rows have id null —
                    // key them by their TSCoach id instead (F3).
                    <AgentStaffCard
                      key={
                        member.id
                          ? `${member.kind || "member"}-${member.id}`
                          : `external-${member.ts_coach_id || index}`
                      }
                      staff={member}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
      {/* Position Filters */}
      <div className=" mx-auto px-4 md:px-8 py-4 md:py-6">
        <PositionFilterDropdown
          positions={positions}
          selectedId={selectedPosition}
          onSelect={setSelectedPosition}
        />
      </div>

      {/* Content */}
      <div className={`mx-auto px-4 md:px-12 mt-4 md:mt-0`}>
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && <div className="text-red-500 text-center py-8">{error}</div>}

        {/* Players Grid */}
        {!loading && !error && (
             <>
            <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 `}>
              {filteredPlayers.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500">
                  {selectedPosition === "all"
                    ? "No players found in your squad"
                    : `No ${selectedPosition} players found`}
                </div>
              ) : (
                sortedPlayers.map((player) => (
                  <AgentPlayerCard
                    key={player.id}
                    player={player}
                    onDetailedPositionsChange={handleDetailedPositionsUpdate}
                    onContractExpiryChange={handleContractExpiryUpdate}
                    canEditPositions={true}
                    lmListing={lmListedByPlayerId.get(player.account_id || player.id || player.player_id) || null}
                  />
                ))
              )}
            </div>

            {/* Pagination */}
            {pagination.count > 10 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => fetchPlayers(pagination.currentPage - 1)}
                  disabled={!pagination.previous}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2">
                  Page {pagination.currentPage} of{" "}
                  {Math.ceil(pagination.count / 10)}
                </span>
                <button
                  onClick={() => fetchPlayers(pagination.currentPage + 1)}
                  disabled={!pagination.next}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default MySquad;
